import { Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { SupportTicketService } from 'src/app/services/support-ticket/support-ticket.service';
import html2canvas from 'html2canvas';

@Component({
	selector: 'app-bug-report-button',
	templateUrl: './bug-report-button.component.html',
	styleUrls: ['./bug-report-button.component.scss'],
})
export class BugReportButtonComponent {
	@ViewChild('modalContainer') modalContainer: ElementRef;

	isModalOpen = false;
	isSubmitting = false;

	priority: 'low' | 'medium' | 'urgent' = 'low';
	title = '';
	description = '';
	screenshotPreview: string | null = null;
	screenshotFile: File | null = null;

	constructor(
		private supportTicketService: SupportTicketService,
		private hostRef: ElementRef,
		private ngZone: NgZone,
	) {}

	openModal() {
		this.isModalOpen = true;
	}

	closeModal() {
		this.isModalOpen = false;
		this.resetForm();
	}

	closeOnBackdrop(event: MouseEvent) {
		if ((event.target as HTMLElement).classList.contains('bug-report-overlay')) {
			this.closeModal();
		}
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files[0]) {
			const file = input.files[0];
			this.screenshotFile = file;
			const reader = new FileReader();
			reader.onload = (e) => {
				this.screenshotPreview = e.target?.result as string;
			};
			reader.readAsDataURL(file);
		}
	}

	async captureScreenshot() {
		this.isModalOpen = false;
		const host: HTMLElement = this.hostRef.nativeElement;
		host.style.visibility = 'hidden';

		const banner = this.createCaptureBanner();
		document.body.appendChild(banner);

		// Let the banner render before capturing
		await new Promise((resolve) => setTimeout(resolve, 300));

		try {
			const canvas = await html2canvas(document.body, {
				useCORS: true,
				allowTaint: true,
				scale: 1,
			});

			// Convert toBlob to a real Promise so we can await it
			const blob = await new Promise<Blob | null>((resolve) =>
				canvas.toBlob((b) => resolve(b), 'image/png'),
			);

			if (blob) {
				this.screenshotFile = new File([blob], 'screenshot.png', { type: 'image/png' });
				this.screenshotPreview = canvas.toDataURL('image/png');
			}
		} catch (_) {
			// capture failed silently
		} finally {
			// Screenshot done — remove banner then reopen modal inside Angular zone
			banner.remove();
			host.style.visibility = '';
			this.ngZone.run(() => (this.isModalOpen = true));
		}
	}

	private createCaptureBanner(): HTMLElement {
		const el = document.createElement('div');
		el.id = 'brm-capture-banner';
		el.innerHTML = `<i class="ri-camera-line me-2"></i> Taking screenshot...`;
		return el;
	}

	clearScreenshot() {
		this.screenshotPreview = null;
		this.screenshotFile = null;
	}

	async submitTicket() {
		if (!this.title.trim()) return;

		this.isSubmitting = true;
		const formData = new FormData();
		formData.append('title', this.title);
		formData.append('description', this.description);
		formData.append('priority', this.priority);
		formData.append('url', window.location.href);
		formData.append('user_id', localStorage.getItem('user_id') || '');

		if (this.screenshotFile) {
			formData.append('screenshot', this.screenshotFile, this.screenshotFile.name);
		}

		this.supportTicketService.createTicket(formData).subscribe({
			next: () => {
				this.isSubmitting = false;
				this.closeModal();
			},
			error: () => {
				this.isSubmitting = false;
			},
		});
	}

	private resetForm() {
		this.priority = 'low';
		this.title = '';
		this.description = '';
		this.screenshotPreview = null;
		this.screenshotFile = null;
	}
}


